import style from "./CoapplicantDetails.module.css";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { addCoApplicant, clearCoApplicantState, coapplicant, removeCoapplicant, selectCoApplicant, setCoapplicantInfo } from "@/slices/coApplicantSlice";
import React from "react";
import { RootState } from "@/store/dtm";
import { AnimatePresence, motion } from "framer-motion";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createApiClient } from "@/lib/Requests";
import { CoApplicantForMortgageDto, CoApplicantForMortgageManipulationDto } from "@/lib/DTMWebAPIClient.generated";
import { Toast } from "primereact/toast";
import { error } from "console";
import dayjs from "dayjs";
import DateComponent from "../shared/DateComponent";
import { setClickInsightFormErrors, transformMonth } from "@/lib/util";
import InputMask from "react-input-mask";

export default function CoapplicantDetails(props: any) {
  const [showEdit, setShowEdit] = useState(false);

  const dispatch = useDispatch();
  const errorToast = useRef<Toast>(null);

  const applicant = useSelector((state: RootState) => state.applicant.applicant);
  const application = useSelector(
    (state: RootState) => state.application.application
  );
  const coapplicants = useSelector(selectCoApplicant).coapplicants;
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

  const methods = useForm({});
  const { register, handleSubmit, formState: { errors }, control, clearErrors } = methods;
  const { fields, append, remove, update } = useFieldArray({
    control: methods.control,
    name: "coapplicants",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [allowSpouse, setAllowSpouse] = useState(true);
  const cachedCoApplicants = useRef<coapplicant[]>([]);
  const [dobYear, setDobYear] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [ageErrorState, setAgeErrorState] = useState({ hasError: false, errorMessage: "", invalidYear: false, invalidDay: false });
  const [phoneNumberText, setPhoneNumberText] = useState<string[]>([]);

  const onError = (errors: object) => {
    setClickInsightFormErrors(errors);
  }

  const onSubmit = async (data: any) => {
    let emails: string[] = [];
    data.coapplicants.forEach((c: coapplicant) => {
      emails.push(c.email)
    });

    //Check if co-applicant emails are unique
    if (new Set(emails).size !== emails.length) {
      data.coapplicants.forEach((c: coapplicant, i: number) => {
        methods.setError(`coapplicants[` + i + `].email`, { type: "custom", message: "Email addresses must be unique." });
      });
      return;
    }

    //Check if co-applicant emails are unique from applicant email
    emails.push(applicant.email);
    if (new Set(emails).size !== emails.length) {
      data.coapplicants.forEach((c: coapplicant, i: number) => {
        if (c.email == applicant.email) {
          methods.setError(`coapplicants[` + i + `].email`, { type: "custom", message: "Email address cannot be the same as the applicant email." });
        }
      });
      return;
    }

    setIsSaving(true);
    setDisplayError(false);
    let errorSaving = false;

    dispatch(clearCoApplicantState());
    remove();

    data.coapplicants.forEach((c: coapplicant, index: number) => {
      //Refresh IDs to avoid duplicates
      c.id = (index + 1).toString();
      dispatch(addCoApplicant(c));
    });

    //Update CoApplicants List in Digital Database
    //Update existing co-applicants
    for (let i = 0; i < cachedCoApplicants.current.length; i++) {
      let c = cachedCoApplicants.current[i];
      let exists = false;
      for (let j = 0; j < cachedCoApplicants.current.length; j++) {
        let d = data.coapplicants[j];
        if (c.inviteCode == d.inviteCode) {
          exists = true;
          await client.apiCoapplicantsUpdate(new CoApplicantForMortgageManipulationDto({
            code: d.inviteCode,
            isActiveLink: true,
            firstName: d.firstName,
            lastName: d.lastName,
            dateOfBirth: dayjs(d.dob_year + "-" + d.dob_month + "-" + d.dob_day),
            phoneNumber: d.phoneNumber,
            emailAddress: d.email,
            relationship: d.relationship,
            mainApplicantName: applicant.firstName + " " + applicant.lastName
          })).then((result) => {
            console.log(result);
          }).catch((error) => {
            console.log("Error saving co-applicant", error);
            if (error.response && error.response.code == 1002) {
              setDisplayError(true);
            }
            errorSaving = true;
          });
        }
      };
      if (!exists) {
        client.apiCoapplicantsDelete(c.inviteCode).then((result) => {
          console.log(result);
        }).catch((error) => {
          console.log("Error deleting co-applicant", error);
          errorSaving = true;
        });
      }
    };

    //Add new co-applicants
    for (let i = 0; i < data.coapplicants.length; i++) {
      const d = data.coapplicants[i];
      let isNewCoApplicant = true;
      for (let j = 0; j < cachedCoApplicants.current.length; j++) {
        const c = cachedCoApplicants.current[j];
        if (d.inviteCode == c.inviteCode) {
          isNewCoApplicant = false;
        }
      }
      if (isNewCoApplicant) {

        try {
          let newCoApplicantResponse = await client.apiCoapplicantsAdd(new CoApplicantForMortgageDto({
            firstName: d.firstName,
            lastName: d.lastName,
            dateOfBirth: dayjs(d.dob_year + "-" + d.dob_month + "-" + d.dob_day),
            phoneNumber: d.phoneNumber,
            emailAddress: d.email,
            relationship: d.relationship,
            mainApplicantName: applicant.firstName + " " + applicant.lastName
          }));

          if (newCoApplicantResponse != undefined) {
            let coapplicantInfo: coapplicant = {
              id: d.id,
              applicantId: d.applicantId,
              relationship: d.relationship,
              firstName: d.firstName,
              lastName: d.lastName,
              dob_year: d.dob_year,
              dob_month: d.dob_month,
              dob_day: d.dob_day,
              phoneNumber: d.phoneNumber,
              email: d.email,
              inviteCode: newCoApplicantResponse.inviteCode ? newCoApplicantResponse.inviteCode : "",
              isInviteActive: true
            }
            console.log("Adding new co-applicant");
            dispatch(setCoapplicantInfo(coapplicantInfo));
          }
        } catch (error: any) {
          console.log("Error creating new co-applicant", error);
          if (error.response && error.response.code == 1002) {
            setDisplayError(true);
          }
          errorSaving = true;
          dispatch(removeCoapplicant(d.id));
        }
      }
    }
    setIsSaving(false);
    if (!errorSaving) {
      closeEdit(false);
    } else {
      data.coapplicants.forEach((c: coapplicant) => {
        append(c);
      });
    }
  };

  const openEdit = () => {
    setShowEdit(true);
    props.editModeOn(!showEdit);
    let phoneNumbers: string[] = [];
    coapplicants.forEach((c: coapplicant) => {
      if (c.relationship == "Spouse") {
        setAllowSpouse(false);
      }
      append(c);
      phoneNumbers.push(c.phoneNumber);
    });
    cachedCoApplicants.current = [...coapplicants];
    setPhoneNumberText(phoneNumbers);
  };

  const closeEdit = (resetCoapplicants: boolean) => {
    setShowEdit(false);
    setDisplayError(false);
    methods.clearErrors();
    remove();

    //Restore original co-applicants if errors exist (or cancel was initiated)
    if (resetCoapplicants) {
      dispatch(clearCoApplicantState());
      cachedCoApplicants.current.forEach((c: coapplicant) => {
        dispatch(addCoApplicant(c));
      });
      cachedCoApplicants.current = [];
    }

    props.editModeOn(!showEdit);
  };

  const setYearHandler = (e: any) => {
    setDobYear(e.target.value)
  };
  const setMonthHandler = (e: any) => {
    setDobMonth(e.target.value)
  };
  const setDayHandler = (e: any) => {
    setDobDay(e.target.value)
  };

  const validateDay = () => {
    if (ageErrorState.invalidDay) {
      return ageErrorState.errorMessage;
    } else {
      return true;
    }
  }

  const validateYear = () => {
    if (ageErrorState.invalidYear) {
      return ageErrorState.errorMessage;
    } else {
      return true;
    }
  }

  const coapplicantList = () => {
    return coapplicants.map((position: any) => {
      return (
        <React.Fragment key={position.id}>
          <>
            <div className="flex flex-row justify-between mt-5">
              <h4>Co-Applicant {position.id}</h4>
            </div>
            <div className="flex md:flex-row flex-col">
              <div className={style.twoColFirst}>
                <p className="mt-5 text-sm">Legal full name</p>
                <h5 className="mt-3">{position.firstName} {position.lastName}</h5>
              </div>
              <div>
                <p className="mt-5 text-sm">Relationship</p>
                <h5 className="mt-3">
                  {position.relationship == "Spouse" ? "Spouse" :
                    position.relationship == "ParentLegalGuardian" ? "Parent/Legal Guardian" :
                      position.relationship == "OtherFamilyMember" ? "Other Family Member" :
                        position.relationship == "Friend" ? "Friend" :
                          position.relationship == "Child" ? "Child" : ""
                  }</h5>
              </div>
            </div>
            <div className="flex md:flex-row flex-col">
              <div className={style.twoColFirst}>
                <p className="mt-5 text-sm">Email address</p>
                <h5 className="mt-3">{position.email}</h5>
              </div>
              <div>
                <p className="mt-5 text-sm">Date of birth</p>
                <h3 className="mt-3" style={{ wordWrap: 'break-word' }}>{position.dob_day} {transformMonth(position.dob_month)}{" "}
                  {position.dob_year}</h3>
              </div>
            </div>
          </>
        </React.Fragment>
      );
    });
  };

  const editableCoapplicantList = () => {
    return (fields.map((field, index) => (
      <div key={field.id}>
        <div className="flex flex-row justify-between mt-5">
          <h4>Co-Applicant {index + 1}</h4>
          <h3 className="brand-blue-text text-xs grid grid-flow-col cursor-pointer" style={{ marginTop: 4 }}>
            <span onClick={() => {
              remove(index)
            }}>Remove</span>
          </h3>
        </div>

        <div className="flex lg:flex-row flex-col">
          <div className={style.twoColFirst}>
            <p className="mt-5 text-sm">Legal first name</p>
            <div className="flex relative">
              <span>
                <img
                  src="assets/error-icon.svg"
                  className={clsx(
                    errors.coapplicants && (errors.coapplicants as any)[index]?.firstName
                      ? ""
                      : "hidden",
                    "w-4 h-4 absolute top-6",
                    style.errorIcon
                  )}
                  alt="error"
                  height={5}
                  width={5}
                />
              </span>
              <input
                className={clsx(
                  errors.coapplicants && (errors.coapplicants as any)[index]?.firstName
                    ? "error"
                    : "",
                  "mt-2",
                  style.jointInput
                )}
                type="text"
                id={`coapplicants.${index}firstName`}
                {...methods.register(`coapplicants.${index}.firstName`, {
                  maxLength: {
                    value: 38,
                    message: "Name exceeds maximum characters",
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9 ]*$/,
                    message: "Enter valid name",
                  },
                  required: "Enter valid name",
                })}
              />
            </div>
            {errors.coapplicants && (errors.coapplicants as any)[index]?.firstName && (
              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                {(errors.coapplicants as any)[index]?.firstName.message}
              </span>
            )}
          </div>
          <div>
            <p className="mt-5 text-sm">Legal last name</p>
            <div className="flex relative">
              <span>
                <img
                  src="assets/error-icon.svg"
                  className={clsx(
                    errors.coapplicants && (errors.coapplicants as any)[index]?.lastName
                      ? ""
                      : "hidden",
                    "w-4 h-4 absolute top-6",
                    style.errorIcon
                  )}
                  alt="error"
                  height={5}
                  width={5}
                />
              </span>
              <input
                className={clsx(
                  errors.coapplicants && (errors.coapplicants as any)[index]?.lastName
                    ? "error"
                    : "",
                  "mt-2",
                  style.jointInput
                )}
                type="text"
                id={`coapplicants.${index}lastName`}
                {...methods.register(`coapplicants.${index}.lastName`, {
                  maxLength: {
                    value: 38,
                    message: "Name exceeds maximum characters",
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9 ]*$/,
                    message: "Enter valid name",
                  },
                  required: "Enter valid name",
                })}
              />
            </div>
            {errors.coapplicants && (errors.coapplicants as any)[index]?.lastName && (
              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                {(errors.coapplicants as any)[index]?.lastName.message}
              </span>
            )}
          </div>
        </div>
        <div className="mt-5">
          <label htmlFor="dobMonth">Date of birth</label>
          <div className="flex mt-3">
            <div className="flex relative">
              <span>
                <img
                  src="assets/error-icon.svg"
                  className={clsx(
                    errors.coapplicants && (errors.coapplicants as any)[index]?.dob_month
                      ? ""
                      : "hidden",
                    "w-4 h-4 absolute top-6",
                    style.errorIcon
                  )}
                  alt="error"
                  height={5}
                  width={5}
                />
              </span>
              <select
                id={`coapplicants.${index}dob_month`}
                {...methods.register(`coapplicants.${index}.dob_month`, {
                  required: "Enter your month of birth",
                })}
                defaultValue=""
                style={{ pointerEvents: props.readOnly ? "none" : undefined }}
                className={clsx(
                  errors.coapplicants && (errors.coapplicants as any)[index]?.dob_month
                    ? "error"
                    : "",
                  "date-component mr-4 pt-2.5"
                )}
                onChange={props.callbackMonth}
                disabled={props.readOnly}
              >
                <option hidden value="">
                  Month
                </option>
                <option value="01">January</option>
                <option value="02">February</option>
                <option value="03">March</option>
                <option value="04">April</option>
                <option value="05">May</option>
                <option value="06">June</option>
                <option value="07">July</option>
                <option value="08">August</option>
                <option value="09">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
            <div className="flex relative">
              <span>
                <img
                  src="assets/error-icon.svg"
                  className={clsx(
                    errors.coapplicants && (errors.coapplicants as any)[index]?.dob_day
                      ? ""
                      : "hidden",
                    "w-4 h-4 absolute top-6",
                    style.errorIcon
                  )}
                  alt="error"
                  height={5}
                  width={5}
                />
              </span>
              <input
                placeholder="DD"
                className={clsx(
                  errors.coapplicants && (errors.coapplicants as any)[index]?.dob_day
                    ? "error"
                    : "",
                  "date-component mr-4"
                )}
                type="text"
                disabled={props.readOnly}
                maxLength={2}
                id={`coapplicants.${index}dob_day`}
                {...methods.register(`coapplicants.${index}.dob_day`, {
                  required: "Enter your date of birth",
                  min: {
                    value: 1,
                    message: "Please enter correct day",
                  },
                  pattern: {
                    value: /^(([0]?[1-9])|([1-2][0-9])|(3[01]))$/,
                    message: "Please enter correct day",
                  },
                  validate: props.validateDay,
                })}
                onChange={props.callbackDay}
              />
            </div>
            <div className="flex relative">
              <span>
                <img
                  src="assets/error-icon.svg"
                  className={clsx(
                    errors.coapplicants && (errors.coapplicants as any)[index]?.dob_year
                      ? ""
                      : "hidden",
                    "w-4 h-4 absolute top-6",
                    style.errorIcon
                  )}
                  alt="error"
                  height={5}
                  width={5}
                />
              </span>
              <input
                placeholder="YYYY"
                className={clsx(
                  errors.coapplicants && (errors.coapplicants as any)[index]?.dob_year
                    ? "error"
                    : "",
                  "date-component mr-4"
                )}
                type="text"
                disabled={props.readOnly}
                maxLength={4}
                id={`coapplicants.${index}dob_year`}
                {...methods.register(`coapplicants.${index}.dob_year`, {
                  required: "Enter your year of birth",
                  min: {
                    value: 1900,
                    message: "Please enter correct year",
                  },
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter correct year",
                  },
                  validate: props.validateYear,
                })}
                onChange={props.callbackYear}
              />
            </div>
          </div>

          <div>
            {" "}
            {((methods.formState?.errors?.month?.message ||
              methods.formState?.errors?.day?.message ||
              methods.formState?.errors?.year?.message || methods.formState?.errors?.date2?.message) && methods.formState?.errors?.year?.type != "validate") && (
                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                  Please enter a valid date of birth
                </span>
              )}

          </div>
          <div>
            {methods.formState?.errors?.year?.type === "validate" && (
              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                {methods.formState?.errors?.year?.message?.toString()}
              </span>
            )}

          </div>
        </div>
        <div className="mt-5">
          <div className="mt-5">
            <label htmlFor="phoneNumber">Phone number</label>
            <div className="flex relative">
              <span>
                <img
                  src="assets/error-icon.svg"
                  className={clsx(
                    methods.formState?.errors?.coapplicants && (methods.formState?.errors?.coapplicants as any)[index]?.phoneNumber
                      ? ""
                      : "hidden",
                    "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                  )}
                  alt="error"
                  height={5}
                  width={5}
                />
              </span>
              <InputMask
                type="tel"
                placeholder="(000) 000-0000"
                alwaysShowMask={false}
                maskChar={null}
                mask="(999) 999-9999"
                id={`coapplicants.${index}phoneNumber`}
                value={phoneNumberText[index]}
                className={clsx(
                  methods.formState?.errors?.coapplicants && (methods.formState?.errors?.coapplicants as any)[index]?.phoneNumber
                    ? "error"
                    : "",
                  "mt-2 w:[320px] md:w-[322px]"
                )}
                {...methods.register(`coapplicants.${index}.phoneNumber`, {
                  required: "Enter a valid phone number",
                  pattern: {
                    value:
                      // eslint-disable-next-line no-useless-escape
                      /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                    message: "Enter a valid phone number",
                  },
                })}
                onChange={(e) => {
                  let newPhoneNumbers = [...phoneNumberText];
                  newPhoneNumbers[index] = e.target.value;
                  setPhoneNumberText(newPhoneNumbers);
                  methods.setValue("phoneNumber", e.target.value);
                }}
              />
            </div>
            {methods.formState?.errors?.coapplicants && (methods.formState?.errors?.coapplicants as any)[index]?.phoneNumber && (
              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                Enter a valid phone number
              </span>
            )}
          </div>
        </div>
        <div className="flex md:flex-row flex-col">
          <div className={style.twoColFirst}>
            <p className="mt-5 text-sm">Email address</p>
            <div className="flex relative">
              <span>
                <img
                  src="assets/error-icon.svg"
                  className={clsx(
                    errors.coapplicants && (errors.coapplicants as any)[index]?.email ? "" : "hidden",
                    "w-4 h-4 absolute top-6",
                    style.errorIcon
                  )}
                  alt="error"
                  height={5}
                  width={5}
                />
              </span>
              <input
                className={clsx(
                  errors.coapplicants && (errors.coapplicants as any)[index]?.email
                    ? "error"
                    : "",
                  "mt-2",
                  style.jointInput
                )}
                type="text"
                id={`coapplicants.${index}email`}
                {...methods.register(`coapplicants.${index}.email`, {
                  maxLength: {
                    value: 38,
                    message: "Name exceeds maximum characters",
                  },
                  pattern: {
                    value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                    message: "Enter valid email",
                  },
                  required: "Enter valid email",
                })}
              />
            </div>
            {errors.coapplicants && (errors.coapplicants as any)[index]?.email && (
              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                {(errors.coapplicants as any)[index]?.email.message}
              </span>
            )}
          </div>
          <div>
            <p className="mt-5 text-sm">Applicant's relationship to you</p>
            <div className="flex relative">
              <span>
                <img
                  src="assets/error-icon.svg"
                  className={clsx(
                    errors.coapplicants && (errors.coapplicants as any)[index]?.relationship
                      ? ""
                      : "hidden",
                    "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]",
                    style.errorIcon
                  )}
                  alt="error"
                  height={5}
                  width={5}
                />
              </span>
              <select
                id="applicantRelationship"
                {...methods.register(`coapplicants.${index}.relationship`, {
                  required: "Select an option"
                })}
                defaultValue=""
                className={clsx(
                  errors.coapplicants && (errors.coapplicants as any)[index]?.relationship ? "error" : "",
                  "mt-2 pt-2.5"
                )}
                style={{ maxWidth: 360 }}
              >
                <option hidden value="">
                  Choose...
                </option>
                {(allowSpouse || coapplicants[index]?.relationship == "Spouse") && <option value="Spouse">Spouse</option>}
                <option value="ParentLegalGuardian">Parent/Legal Guardian</option>
                <option value="OtherFamilyMember">Other Family Member</option>
                <option value="Friend">Friend</option>
                <option value="Child">Child 18+</option>
              </select>
            </div>
            {errors.coapplicants && (errors.coapplicants as any)[index]?.relationship && (
              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                {(errors.coapplicants as any)[index]?.relationship.message}
              </span>
            )}
          </div>
        </div>
      </div>
    )))
  };

  return (
    <>
      <hr className="mt-5" />
      <Toast ref={errorToast} />
      <div className="flex flex-row justify-between mt-5">
        <h2>Co-applicants</h2>
        {!showEdit && (
          <h3
            className="brand-blue-text text-xs grid grid-flow-col cursor-pointer"
            onClick={openEdit}
          >
            <img
              src="assets/edit-icon.svg"
              className={style.editIcon}
              alt="edit"
            />
            <span>Edit</span>
          </h3>
        )}
      </div>
      {displayError &&
        <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
          <img src="assets/error-info-icon.svg" alt="error" />
          <span className="text-sm ml-2 mt-1">
            This email address is used for another application; please use an alternative email address.
          </span>
        </div>
      }
      <div className={clsx(style.caContainer, "mt-5")}>
        <AnimatePresence mode="wait" initial={false}>
          {!showEdit ? (
            <motion.div
              key="8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}>
              {coapplicantList()}
            </motion.div>
          ) : (
            <motion.div
              key="9"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}>
              <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
                {editableCoapplicantList()}
                {isSaving &&
                  <div className="flex justify-center">
                    <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 24 }} className={clsx(isSaving ? "" : "hidden")} />
                  </div>
                }
                {fields.length < 3 && !isSaving && (<div className="mb-5">
                  <button
                    type="button"
                    onClick={() => append({ id: coapplicants.length + 1 + "" })}
                    className="secondary mt-5 w-full sm:w-28 h-11 px-6"
                    style={{ width: '-webkit-fill-available', color: 'var(--brand-blue)' }}>
                    Add co-applicant
                  </button>
                </div>)}
                {!isSaving &&
                  <>
                    <div className="flex max-sm:hidden">
                      <div className="mb-5">
                        <button
                          className="primary mt-5 w-169 sm:w-28 h-11 px-6"
                          type="button"
                          onClick={() => closeEdit(true)}
                        >
                          Cancel
                        </button>
                      </div>
                      <div className="mb-5">
                        <button
                          type="submit"
                          className="primary mt-5 w-169 sm:w-28 h-11 px-6 mx-5"
                          disabled={isSaving}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                          Save
                        </button>
                      </div>
                    </div>
                    <div className="flex sm:hidden">
                      <div className="mb-5">
                        <button
                          className="primary mt-5 w-169 sm:w-28 h-11 px-6"
                          type="button"
                          onClick={() => closeEdit(true)}
                          style={{ minWidth: 90 }}
                        >
                          Cancel
                        </button>
                      </div>
                      <div className="mb-5">
                        <button
                          type="submit"
                          className="primary mt-5 w-169 sm:w-28 h-11 px-6 mx-5"
                          style={{ minWidth: 90 }}
                          disabled={isSaving}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                          Save
                        </button>
                      </div>
                    </div>
                  </>}

              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
